import type { CSSProperties, ImgHTMLAttributes } from 'react';

import { convexHttpBase } from '@/lib/api-client';

type ImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'width' | 'height'> & {
  src: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  /** Next's `fill`: absolutely fill the nearest positioned ancestor. */
  fill?: boolean;
  priority?: boolean;
  quality?: number;
  unoptimized?: boolean;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
};

const FILL_STYLE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

/**
 * `next/image`'s call shape over a plain `<img>`.
 *
 * The static host does not resize or re-encode, so `quality`, `placeholder`,
 * and `unoptimized` are accepted and ignored — dropping them at the call sites
 * would be a 23-file diff for no behaviour change. `priority` maps to
 * `fetchPriority="high"` plus eager loading, which is what it actually bought;
 * everything else lazy-loads, matching Next's default.
 *
 * Responsive sizing is not lost with the Next server, because the images do not
 * come from this origin: Unsplash and Pexels both resize from a query
 * parameter, and both already negotiate WebP/AVIF through `auto=`. The shim
 * builds a `srcSet` against those parameters, so a phone stops downloading the
 * 1600px (Unsplash) or 2400px (Pexels) original that the stored URLs ask for.
 *
 * It is also the single seam where a persisted `/api/...` image URL is resolved
 * to the Convex host. Signed place-photo URLs are stored relative in planning
 * payloads and public snapshots, and there is no server on this origin to serve
 * them any more.
 */
export function Image({
  fill,
  priority,
  quality: _quality,
  unoptimized: _unoptimized,
  placeholder: _placeholder,
  blurDataURL: _blurDataURL,
  style,
  loading,
  ...rest
}: ImageProps) {
  return (
    // `alt` is required by ImageProps and reaches the element through `rest`.
    <img
      {...rest}
      src={resolveImageSrc(rest.src)}
      srcSet={rest.srcSet ?? buildSrcSet(rest.src)}
      style={fill ? { ...FILL_STYLE, ...style } : style}
      loading={loading ?? (priority ? 'eager' : 'lazy')}
      fetchPriority={priority ? 'high' : undefined}
      decoding="async"
    />
  );
}

export default Image;

/**
 * Persisted image URLs are same-origin paths minted when a Next server served
 * this origin. Point them at the Convex HTTP host once one is configured; until
 * then they resolve as before.
 */
export function resolveImageSrc(src: string): string {
  if (!src.startsWith('/api/')) return src;
  const base = convexHttpBase();
  return base ? `${base}${src}` : src;
}

/**
 * Width ladder for generated `srcSet` candidates, covering common device widths
 * at 1x and 2x. Mirrors the shape of Next's default image widths.
 */
const SRCSET_WIDTHS = [640, 828, 1080, 1200, 1600, 2048] as const;

/**
 * Hosts that resize from a query parameter. Only these are rewritten — guessing
 * at a CDN that ignores the parameter would emit candidates that all return the
 * same bytes, making things worse rather than better.
 */
const RESIZE_PARAM_BY_HOST: Record<string, string> = {
  'images.unsplash.com': 'w',
  'images.pexels.com': 'w',
};

/**
 * A `srcSet` across {@link SRCSET_WIDTHS}, or undefined when the URL's host has
 * no known resize parameter.
 *
 * Never rewrites a `/api/` URL. The place-photo proxy signs `max_height` into
 * its HMAC, so changing the requested size would invalidate the signature and
 * turn every photo into a 404.
 */
export function buildSrcSet(src: string): string | undefined {
  if (!src || src.startsWith('/') || src.startsWith('data:')) return undefined;

  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return undefined;
  }

  const param = RESIZE_PARAM_BY_HOST[url.hostname];
  if (!param) return undefined;

  // Never ask for more than the stored URL already requested: the extra bytes
  // would be real and the extra detail would not.
  const declared = Number(url.searchParams.get(param));
  const ceiling = Number.isFinite(declared) && declared > 0 ? declared : Infinity;
  const widths = SRCSET_WIDTHS.filter((width) => width <= ceiling);
  if (widths.length === 0) return undefined;

  return widths
    .map((width) => {
      const candidate = new URL(url.href);
      candidate.searchParams.set(param, String(width));
      // A fixed height would pin the aspect ratio and defeat the width ladder.
      candidate.searchParams.delete('h');
      return `${candidate.href} ${width}w`;
    })
    .join(', ');
}
